import React, { useState, useEffect } from "react";
import { getRequest, patchRequest, postRequest } from "../../axiosRoutes/index";
import {
    Button,
    Modal,
    Box,
    Typography,
    OutlinedInput,
    FormControl,
    InputLabel,
    Grid,
    TextField,
    Hidden,
    CircularProgress,
    Snackbar,
    Alert,
} from "@mui/material";
import { useSnackbar } from "../../axiosRoutes/snackbarConfig";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const AddBankDetails = ({ open, handleClose, bankId }) => {
    const { snackbar, showSnackbar, handleSnackbarClose } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [bankData, setBankData] = useState({
        title: "",
        details: "",
    });

    useEffect(() => {
        const fetchBank = async () => {
            if (bankId !== null) {
                const access = localStorage.getItem("access");
                try {
                    setLoading(true);
                    const response = await getRequest(`admin/bank/${bankId}`, {
                        headers: {
                            Authorization: `Bearer ${access}`,
                        },
                    });
                    setBankData({
                        title: response.title,
                        details: response.details,
                    });
                } catch (error) {
                    console.error("Error fetching Bank Details:", error);
                } finally {
                    setLoading(false);
                }
            } else {
                setBankData({
                    title: "",
                    details: "",
                });
            }
        };
        fetchBank();
    }, [bankId]);

    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const access = localStorage.getItem("access");
            await patchRequest(
                `admin/bank/${bankId}`,
                {
                    Authorization: `Bearer ${access}`,
                },
                bankData
            );
            showSnackbar("Bank Details Updated Successfully", "success");
            handleClose();
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || error.message || "An error occurred";
            showSnackbar(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setBankData({ ...bankData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const access = localStorage.getItem("access");
            await postRequest(
                "admin/bank",
                {
                    Authorization: `Bearer ${access}`,
                },
                bankData
            );

            showSnackbar("Bank Details Added Successfully", "success");
            setBankData({
                title: "",
                details: "",
            });
            handleClose();
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || error.message || "An error occurred";
            showSnackbar(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: '80%', md: '40%' },
                        maxHeight: "80vh",
                        overflowY: "auto",
                        bgcolor: "black",
                        color: "#FEFEFE",
                        border: "1px solid #A9A9A9",
                        p: 2,
                        "& .MuiOutlinedInput-root": {
                            color: "#FEFEFE",
                            "& fieldset": {
                                borderColor: "#FEFEFE",
                            },
                            "&:hover fieldset": {
                                borderColor: "#FEFEFE",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "#FEFEFE",
                            },
                        },
                        "& .MuiInputLabel-root": {
                            color: "#FEFEFE",
                        },
                        "& .MuiIconButton-root": {
                            color: "#FEFEFE",
                        },
                    }}
                >
                    <Grid container spacing={2}>
                        <Hidden mdDown>
                            <Grid item md={4}>
                                <AccountBalanceIcon
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        borderRadius: "8px",
                                        color: "#E5622D",
                                    }}
                                />
                            </Grid>
                        </Hidden>
                        <Grid item xs={12} md={8}>
                            <Typography
                                id="modal-modal-title"
                                variant="h4"
                                fontWeight="bold"
                                textAlign="center"
                                component="h1"
                                sx={{ mb: 2 }}
                            >
                                {bankId ? "Update Bank Details" : "Add Bank Details"}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                                        <InputLabel htmlFor="outlined-adornment-title">
                                            Account Title
                                        </InputLabel>
                                        <OutlinedInput
                                            id="outlined-adornment-title"
                                            type="text"
                                            name="title"
                                            label="Account Title"
                                            value={bankData.title}
                                            onChange={handleChange}
                                            sx={{ backgroundColor: "transparent" }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        id="bank-details"
                                        label="Bank Details (Comma-Separated)"
                                        name="details"
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        maxRows={10}
                                        value={bankData.details}
                                        onChange={handleChange}
                                        sx={{
                                            mb: 1,
                                            backgroundColor: "transparent",
                                            "& .MuiInputBase-root": {
                                                overflow: "auto",
                                            },
                                        }}
                                    />
                                </Grid>

                                {bankId !== null ? (
                                    <Grid item xs={6}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={handleUpdate}
                                            sx={{
                                                bgcolor: "#E5622D",
                                                "&:hover": { bgcolor: "#D0521B" },
                                            }}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                                            ) : (
                                                "Update"
                                            )}
                                        </Button>
                                    </Grid>
                                ) : (
                                    <Grid item xs={6}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={handleSubmit}
                                            sx={{
                                                bgcolor: "#E5622D",
                                                "&:hover": { bgcolor: "#D0521B" },
                                            }}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                                            ) : (
                                                "Add"
                                            )}
                                        </Button>
                                    </Grid>
                                )}
                                <Grid item xs={6}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleClose}
                                        sx={{ bgcolor: "#A9A9A9", "&:hover": { bgcolor: "#E1E1E1" } }}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddBankDetails;
